import ActionButton from 'components/ActionButton';
import React, { useState } from 'react';
import { createExternalOrder, quoteOrder } from 'services';
import { MODAL_TYPES, showError, showSuccess, showWarning } from 'utils';
import styles from './index.module.scss';
import OrderItems from './orderItems';
import _ from 'lodash';
import { IExternalOrder } from 'interfaces/externalOrder';
import CustomerChannel from './CustomerChannel';
import SavedPaymentMethods from 'components/SavedPaymentMethods';
import InfoModal from 'components/InfoModal';
import Currency from 'components/Currency/Currency';

const CreateExternalOrder = props => {
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingPaymentInfo, setLoadingPaymentInfo] = useState<boolean>(false);
  const [selectedChannel, setSelectedChannel] = useState<Record<string, any>>({});
  const [selectedChannelName, setSelectedChannelName] = useState<string>('');
  const [items, setItems] = useState<IExternalOrder.IExternalOrderItemType[]>
    ([{ id: '', name: '', quantity: 0, price: 0, discountPercent: '', fixedDiscountAmount: '' }]);
  const [address, setAddress] = useState<string>('');
  const [addressModalOpen, setAddressModalOpen] = useState<boolean>(false);
  const [latitude, setLatitude] = useState<string>('');
  const [longitude, setLongitude] = useState<string>('');
  const [contactName, setContactName] = useState<string>('');
  const [contactPhone, setContactPhone] = useState<string>('');
  const [exemptDeliveryCharges, setExemptDeliveryCharges] = useState<boolean>(false);
  const [notes, setNotes] = useState<string>('');
  const [operatorComment, setOperatorComment] = useState<string>('');
  const [payLater, setPayLater] = useState<boolean>(false);
  const [alreadyPaid, setAlreadyPaid] = useState<boolean>(false);
  const [updateOrderQuote, setUpdateOrderQuote] = useState<boolean>(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState<string>('');
  const [successModalOpen, setSuccessModalOpen] = useState<boolean>(false);
  const [orderId, setOrderId] = useState<string>('');
  const [userId, setUserId] = useState<string>('');
  const [paymentInfo, setPaymentInfo] = useState<Record<string, any>>({
    subtotal: 0,
    taxes: 0,
    deliveryTaxes: 0,
    deliveryCharges: 0,
  });
  const [total, setTotal] = useState<number>();
  const [selectedAddressId, setSelectedAddressId] = useState<string>();

  const createExternalOrderFn = async () => {
    if (!selectedChannel.objectId) {
      showWarning('Please select a Sales Channel');
      return;
    }
    if (!address) {
      showWarning('Please select a Delivery Address');
      return;
    }
    if (items.length === 1 && items[0].id === '') {
      showWarning('Please add at least one Inventory Item');
      return;
    }
    if (!total || updateOrderQuote) {
      showWarning(`Please ${updateOrderQuote && total ? 'Re-' : ''}Quote the Order Charges`);
      return;
    }
    if (!payLater && !alreadyPaid && !selectedPaymentId) {
      showWarning('Please select a Payment Method');
      return;
    }
    setLoading(true);
    let orderData: Record<string, any> = {
      inventoryItems: items.map(item => ({
        id: item.id,
        price: item.price,
        quantity: item.quantity,
        discountPercent: parseFloat(item.discountPercent),
        fixedDiscountAmount: parseFloat(item.fixedDiscountAmount),
      })),
      userId: selectedChannel.objectId,
      contactName,
      contactPhone,
      deliveryAddress: {
        address,
        coords: {
          latitude: parseFloat(latitude),
          longitude: parseFloat(longitude),
        },
      },
      subtotals: paymentInfo.subtotal,
      taxes: paymentInfo.taxes,
      deliveryCharges: paymentInfo.deliveryCharges,
      deliveryTaxes: paymentInfo.deliveryTaxes,
      total: parseFloat((total || 0).toFixed(2)),
      notes,
      operatorComment,
      selectedAddressId,
    };

    if (alreadyPaid) {
      orderData = {
        ...orderData,
        alreadyPaid,
      };
    } else {
      if (!payLater) {
        orderData = {
          ...orderData,
          selectedPaymentId,
        };
      } else {
        orderData = {
          ...orderData,
          payLater,
        };
      }

    }

    try {
      const response = await createExternalOrder(orderData);
      setOrderId(response?.objectId);
      showSuccess('Order created successfully.');
      setSuccessModalOpen(true);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentInfo = async () => {
    setLoadingPaymentInfo(true);
    const quoteOrderData: Record<string, any> = {
      exemptDeliveryCharges,
      cartItems: items.map(item => {
        return {
          objectId: item.id,
          amount: item.quantity,
          discountPercent: parseFloat(item.discountPercent),
          fixedDiscountAmount: parseFloat(item.fixedDiscountAmount),
        };
      }),
      delivery: true,
      userId: selectedChannel.objectId,
      addressObject: {
        coords: {
          latitude,
          longitude,
        },
      },
    };
    try {
      const response = await quoteOrder(quoteOrderData);
      const {
        deliveryCharges,
        deliveryTaxes,
        subtotals,
        taxes,
        total,
      } = response;
      setPaymentInfo({
        taxes,
        deliveryTaxes,
        deliveryCharges,
        subtotal: subtotals,
      });
      setTotal(total);
      setUpdateOrderQuote(false);
    } catch (e) {
      showError(e);
    } finally {
      setLoadingPaymentInfo(false);
    }
  };

  const setAddressInfo = (addressDetails: Record<string, any>) => {
    if (addressDetails.objectId) {
      setSelectedAddressId(addressDetails.objectId);
    }
    setAddress(addressDetails.address);
    setLongitude(addressDetails.longitude);
    setLatitude(addressDetails.latitude);
    setContactName(addressDetails.contactName);
    setContactPhone(addressDetails.contactPhone);
  };

  const getFooterSection = () => {
    const getValidItems = () => {
      const itemArr = items.filter(item => item.id.length);
      return itemArr;
    };
    return (
      <div className={styles['full-footer-container']}>
        <div className={styles['footer-container']}>
          <div className={styles['footer-details-container']}>
            <div className={styles['sales-channel-name']}>
              <div className={styles['label']}>Sales Channel Name</div>
              <div className={styles['name']}>{selectedChannelName || '-'}</div>
            </div>
            <div className={styles['total-items']}>
              <div className={styles['label']}>Total Items</div>
              <div className={styles['items']}>{getValidItems().length}</div>
            </div>
            <div className={styles['total-quantity']}>
              <div className={styles['label']}>Total Quantity</div>
              <div className={styles['items']}>{_.sumBy(getValidItems(), 'quantity')}</div>
            </div>
            <div className={styles['total-price']}>
              <div className={styles['label']}>Total Price</div>
              <div className={styles['price']}><Currency amount={total} default='0' /></div>
            </div>
            <div className={styles['po-btn-for-desktop']}>
              <div className={styles['create-order-button']}>
                <ActionButton
                  text={'Cancel'}
                  active={!loading}
                  isLoading={false}
                  onClick={() => props.history.goBack()}
                  customClass={styles['cancel']}
                />
                <ActionButton
                  active={!loading}
                  isLoading={loading}
                  onClick={createExternalOrderFn}
                  customClass={'form-control'}
                  text={'Create External Order'}
                />
              </div>
            </div>
          </div>
        </div>
        <div className={styles['po-btn-for-mobile']}>
          <div className={'d-flex mx-0 my-3 justify-content-between'}>
            <ActionButton
              text={'Cancel'}
              active={!loading}
              isLoading={false}
              onClick={() => props.history.goBack()}
              customClass={styles['cancel']}
            />
            <ActionButton
              active={!loading}
              isLoading={loading}
              onClick={createExternalOrderFn}
              customClass={'form-control m-0'}
              text={'Create External Order'}
            />
          </div>
        </div>
      </div >
    );
  };

  const getNotesSection = () => (
    <div className={styles['notes-section']}>
      <div className={styles['section-header']}>
        <div className={styles['section-heading']}>Order Notes</div>
      </div>
      <div className={styles['textarea-group']}>
        <div className={styles['label']}>Notes</div>
        <textarea
          className={'form-control'}
          disabled={loading}
          value={notes}
          onChange={e => setNotes(e.target.value)}
        />
      </div>
      <div className={styles['textarea-group']}>
        <div className={styles['label']}>Operator Comments</div>
        <textarea
          className={'form-control'}
          disabled={loading}
          value={operatorComment}
          onChange={e => setOperatorComment(e.target.value)}
        />
      </div>
    </div>
  );

  const getPaymentMethodsSection = () => (
    <div className={styles['payment-methods-section']}>
      <div className={styles['section-header']}>
        <div className={styles['section-heading']}>Payment Methods</div>
        <div className={styles['pay-later']}>
          <input
            type={'checkbox'}
            name={'pay-later'}
            disabled={loading}
            checked={payLater}
            onChange={e => {
              setSelectedPaymentId('');
              setAlreadyPaid(false);
              setPayLater(e.target.checked);
            }}
          />
          <div className={styles['pay-later-text']}>Pay Later</div>
        </div>
        <div className={styles['already-paid']}>
          <input
            type={'checkbox'}
            name={'already-paid'}
            disabled={loading}
            checked={alreadyPaid}
            onChange={e => {
              setSelectedPaymentId('');
              setPayLater(false);
              setAlreadyPaid(e.target.checked);
            }}
          />
          <div className={styles['already-paid-text']}>Already Paid</div>
        </div>
      </div>
      <SavedPaymentMethods
        user={selectedChannel}
        disabled={alreadyPaid || payLater}
        selectedPaymentId={selectedPaymentId}
        setPaymentId={val => setSelectedPaymentId(val)}
      />
    </div>
  );

  const getPaymentInfoSection = () => {
    return (
      <div className={styles['payment-info-container']}>
        <div className={styles['section-header']}>
          <div className={styles['section-heading']}>Payment Information</div>
          <div className={styles['exempt-delivery']}>
            <input
              type={'checkbox'}
              name={'exempt-delivery'}
              disabled={loading}
              onChange={e => setExemptDeliveryCharges(e.target.checked)}
            />
            <div className={styles['exempt-delivery-text']}>Exempt Delivery Charges</div>
          </div>
          <ActionButton
            text={'Quote Order'}
            active={!loadingPaymentInfo}
            isLoading={loadingPaymentInfo}
            onClick={fetchPaymentInfo}
          />
        </div>
        <div className={styles['delivery-charge']}>
          <div className={styles['delivery-charges-container']}>
            <div className={styles['label']}>Delivery Charges</div>
            <input
              name={'deliveryCharges'}
              value={paymentInfo.deliveryCharges || '0'}
              disabled={true}
              className={'form-control'}
            />
          </div>
          <div className={styles['delivery-taxes-container']}>
            <div className={styles['label']}>Delivery Taxes</div>
            <input
              name={'deliveryTaxes'}
              value={paymentInfo.deliveryTaxes || '0'}
              disabled={true}
              className={'form-control'}
            />
          </div>
        </div>
        <div className={styles['overall-charge']}>
          <div className={styles['subtotal-container']}>
            <div className={styles['label']}>Subtotal</div>
            <input
              name={'subtotal'}
              value={paymentInfo.subtotal || '0'}
              disabled={true}
              className={'form-control'}
            />
          </div>
          <div className={styles['taxes-container']}>
            <div className={styles['label']}>Taxes</div>
            <input
              name={'taxes'}
              value={paymentInfo.taxes || '0'}
              disabled={true}
              className={'form-control'}
            />
          </div>
        </div>
        <div className={styles['total-container']}>
          <div className={styles['label']}>Total</div>
          <input
            name={'total'}
            value={total || '0'}
            disabled={true}
            className={'form-control'}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      {successModalOpen && (
        <InfoModal
          type={MODAL_TYPES.SUCCESS}
          title={'External Order'}
          message={'The order has been created successfully.'}
          toggleModal={() => setSuccessModalOpen(!successModalOpen)}
          primaryButtonText={'Go to Order Details'}
          primaryButtonOnClick={() => {
            setSuccessModalOpen(false);
            props.history.replace(`/orders/${orderId}`);
          }}
          secondaryButtonText={'Go to Orders List'}
          secondaryButtonOnClick={() => {
            setSuccessModalOpen(false);
            props.history.replace('/orders');
          }}
        />
      )}
      <div className={`${styles['main-container']}`}>
        <div className={styles['header']}>
          <div className={styles['heading']}>Create Manual Order</div>
        </div>
        <div className={styles['details-container']}>
          <CustomerChannel
            loading={loading}
            channel={selectedChannel}
            setChannel={setSelectedChannel}
            setSelectedChannelName={setSelectedChannelName}
            addressModalOpen={addressModalOpen}
            setUserId={setUserId}
            setAddress={setAddressInfo}
            toggleAddressModal={() => setAddressModalOpen(!addressModalOpen)}
          />
          <OrderItems
            disabled={loading}
            items={items}
            userId={userId}
            setItems={setItems}
            setRequoteOrderFlag={() => setUpdateOrderQuote(true)}
          />
          {getPaymentInfoSection()}
          {getPaymentMethodsSection()}
          {getNotesSection()}
        </div>
        {getFooterSection()}
      </div>
    </>
  );
};

export { CreateExternalOrder };
